/**
 * Allergen Information System
 * 
 * This module handles allergen calculation and aggregation for products,
 * combining allergen data from:
 * 1. Products themselves (recipe/preparation allergens)
 * 2. Linked stock items (ingredient allergens)
 * 3. Selected product options (customization allergens)
 */

import {
    Product,
    Stock,
    AllergenInfo,
    ComputedAllergenSummary,
    SelectedOption,
    ProductOptionChoice
} from '../types';

/**
 * Calculate all allergens for a product configuration
 * 
 * @param product - The product to calculate allergens for
 * @param stocks - All available stock items
 * @param selectedOptions - Customer's selected options (optional)
 * @returns Complete allergen summary with breakdown by source
 */
export function calculateProductAllergens(
    product: Product | null | undefined,
    stocks: Stock[] | null | undefined,
    selectedOptions?: SelectedOption[]
): ComputedAllergenSummary {
    // Handle null/undefined inputs
    if (!product) {
        return {
            directAllergens: [],
            stockAllergens: [],
            optionAllergens: new Map(),
            allAllergens: []
        };
    }

    const stockMap = new Map((stocks || []).map(s => [s.id, s]));

    // 1. Direct product allergens (from recipe/preparation)
    const directAllergens = product.allergens || [];

    // 2. Allergens from linked stock items
    const stockAllergens: AllergenInfo[] = [];
    for (const stockId of product.stockIds || []) {
        const stock = stockMap.get(stockId);
        if (stock?.allergens) {
            stockAllergens.push(...stock.allergens);
        }
    }

    // 3. Allergens from selected options
    const optionAllergens = new Map<string, AllergenInfo[]>();
    if (selectedOptions) {
        for (const selection of selectedOptions) {
            const option = product.options.find(o => o.id === selection.optionId);
            if (!option) continue;

            for (const choiceId of selection.choiceIds) {
                const choice = option.choices.find(c => c.id === choiceId);
                if (!choice) continue;

                const choiceAllergens: AllergenInfo[] = [];

                // Add choice's direct allergens
                if (choice.allergens) {
                    choiceAllergens.push(...choice.allergens);
                }

                // Add allergens from choice's stock items
                if (choice.stockIds) {
                    for (const stockId of choice.stockIds) {
                        const stock = stockMap.get(stockId);
                        if (stock?.allergens) {
                            choiceAllergens.push(...stock.allergens);
                        }
                    }
                }

                if (choiceAllergens.length > 0) {
                    optionAllergens.set(choiceId, choiceAllergens);
                }
            }
        }
    }

    // 4. Deduplicate and merge all allergens
    const allAllergens = deduplicateAllergens([
        ...directAllergens,
        ...stockAllergens,
        ...Array.from(optionAllergens.values()).flat()
    ]);

    return {
        directAllergens,
        stockAllergens,
        optionAllergens,
        allAllergens
    };
}

/**
 * Deduplicate allergens, keeping highest severity
 * 
 * When the same allergen appears multiple times with different severities,
 * keeps the most severe level: contains > may_contain > traces
 * 
 * @param allergens - Array of allergen info to deduplicate
 * @returns Deduplicated array with highest severity for each allergen
 */
export function deduplicateAllergens(allergens: AllergenInfo[]): AllergenInfo[] {
    const map = new Map<string, AllergenInfo>();
    const severityOrder: Record<AllergenInfo['severity'], number> = {
        'contains': 3,
        'may_contain': 2,
        'traces': 1
    };

    for (const allergen of allergens) {
        const existing = map.get(allergen.allergen);
        if (!existing || severityOrder[allergen.severity] > severityOrder[existing.severity]) {
            map.set(allergen.allergen, allergen);
        }
    }

    // Sort alphabetically by allergen name for consistent display
    return Array.from(map.values()).sort((a, b) =>
        a.allergen.localeCompare(b.allergen)
    );
}

/**
 * Get a human-readable display name for an allergen
 * 
 * @param allergen - Allergen type
 * @returns Formatted display name
 */
export function getAllergenDisplayName(allergen: string): string {
    const displayNames: Record<string, string> = {
        'celery': 'Celery',
        'gluten': 'Gluten (Cereals)',
        'crustaceans': 'Crustaceans',
        'eggs': 'Eggs',
        'fish': 'Fish',
        'lupin': 'Lupin',
        'milk': 'Milk',
        'molluscs': 'Molluscs',
        'mustard': 'Mustard',
        'nuts': 'Tree Nuts',
        'peanuts': 'Peanuts',
        'sesame': 'Sesame',
        'soya': 'Soya',
        'sulphites': 'Sulphites'
    };

    return displayNames[allergen] || allergen.charAt(0).toUpperCase() + allergen.slice(1);
}

/**
 * Get a human-readable display name for allergen severity
 * 
 * @param severity - Allergen severity level
 * @returns Formatted display text
 */
export function getSeverityDisplayName(severity: AllergenInfo['severity']): string {
    const displayNames: Record<AllergenInfo['severity'], string> = {
        'contains': 'Contains',
        'may_contain': 'May contain',
        'traces': 'May contain traces of'
    };

    return displayNames[severity];
}

/**
 * Check if a product or configuration contains specific allergens
 * 
 * @param allergenSummary - Computed allergen summary
 * @param allergenTypes - Allergen types to check for
 * @returns True if any of the specified allergens are present
 */
export function containsAllergens(
    allergenSummary: ComputedAllergenSummary,
    allergenTypes: string[]
): boolean {
    return allergenSummary.allAllergens.some(a =>
        allergenTypes.includes(a.allergen)
    );
}
